import { useTranslation } from 'react-i18next'

import { htmlToPlainText } from '@percent/utility'
import { Badge, TableCell, TableRow } from '@percent/lemonade'
import { getFormattedDateOnly } from '@percent/partner-dashboard/common/library/utility/date'
import { GivingAction } from '@percent/partner-dashboard/services/givingActions/givingActionsService.types'
import { GivingActionsActionsMenu } from './GivingActionsActionsMenu/GivingActionsActionsMenu'
import styles from './GivingActionsTableRow.module.scss'

export type GivingActionsTableRowProps = Readonly<{
  givingAction: GivingAction
  canManage: boolean
  formatReward: (points: number | null | undefined, rewardsEligible: boolean) => string | undefined
  isRewardLoading: boolean
}>

export function GivingActionsTableRow({
  givingAction,
  canManage,
  formatReward,
  isRewardLoading
}: GivingActionsTableRowProps) {
  const { t } = useTranslation()

  const isArchived = !!givingAction.archivedAt
  const isRecurring = givingAction.frequency === 'daily'
  const rewardValue = formatReward(givingAction.pointValue, givingAction.rewardsEligible)

  return (
    <TableRow data-testid={`giving-action-row-${givingAction.id}`}>
      <TableCell>{givingAction.name}</TableCell>
      <TableCell>
        <span className={styles.description}>{htmlToPlainText(givingAction.description)}</span>
      </TableCell>
      <TableCell>
        <Badge variant={isRecurring ? 'informative' : 'default'}>
          {t(`typography.givingActions.frequency.${givingAction.frequency}`)}
        </Badge>
      </TableCell>
      <TableCell data-testid={`giving-action-reward-${givingAction.id}`}>
        {isRewardLoading
          ? null
          : rewardValue ?? t('typography.givingActions.points', { count: givingAction.pointValue })}
      </TableCell>
      <TableCell>
        <Badge variant={isArchived ? 'default' : 'positive'}>
          {t(isArchived ? 'typography.givingActions.status.archived' : 'typography.givingActions.status.active')}
        </Badge>
      </TableCell>
      <TableCell>{getFormattedDateOnly(givingAction.createdAt)}</TableCell>
      <TableCell className={styles.actionsCell}>
        {canManage && <GivingActionsActionsMenu givingAction={givingAction} />}
      </TableCell>
    </TableRow>
  )
}
